import React, { Component, SyntheticEvent } from 'react';

import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import styled from 'styled-components';
import { replace } from 'redux-first-history';
import { convertToLocalTime } from '@dev/ee-tools';
import { parse } from 'query-string';

import {
    Typography,
    DateDropdown,
    FilterIcon,
    SimplePopover,
    SimpleLoader,
    Radio,
    LineChart,
    FiltersPopover,
    palette,
    SimplePopoverPlainOption,
    FilterButton,
} from '@dev/ee-components';

import { truncate, map, forEach, isEqual, isEmpty, sortBy, clone, flatten } from 'lodash-es';
import dayjs from '@api/services/tools/dayjs';

import { LoadSummaryStart, LoadSummaryStartParams } from '@api/store/statistics/actions';
import { ClearChannels, LoadChannelsStart } from '@api/store/channels/actions';
import { ApplicationState as State } from '@api/store';
import StatisticsSummaryInterface, { DateDropdownOptions } from '../StatisticsSummaryInterface';
import { DispatchProps, SummaryGraphContentState } from './GraphContentInterface';
import { chartTypes, statusTypes } from '../../StatisticsInterface';
import { LONG_DATE_FORMAT } from '@api/services/constants';
import { addParamToUrl, parseUrlParams, setAtEndOfDay } from '@api/services/tools/handleUrlParams';
import { Dayjs } from 'dayjs';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { GetCampaigns } from '@api/store/campaigns/actions';
import { getDateDropdownValue } from '@api/services/tools/getDateDropdownValue';
import { INITIAL_PER_PAGE } from '@api/store/exports/types';
import { debounce } from '@api/services/tools/debounce';

const DATE_FORMAT = 'YYYY-MM-DD';
const BOTTOM_END = 'bottom-end';

const mapState = (state: State, ownProps: StatisticsSummaryInterface): StatisticsSummaryInterface => {
    return {
        ...ownProps,
        browser: state.browser,
        statistics: state.statistics,
        channels: state.channels,
        account: state.account,
        campaigns: state.campaigns,
    };
};

const dispatchProps = {
    LoadSummaryStart,
    LoadChannelsStart,
    Replace: replace,
    GetCampaigns,
    ClearChannels,
};

type Props = StatisticsSummaryInterface & DispatchProps;

const GraphContent = styled.div`
    width: 100%;
    min-height: 540px;
    margin-top: 3rem;
    padding: 24px 32px;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    & .legendCell {
        height: 81px;
    }
`;

const GraphHeader = styled.div`
    display: flex;
    justify-content: space-between;
    .channelName {
        max-width: 50%;
        & > div {
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

const GraphFilters = styled.div`
    display: flex;
    justify-content: space-between;
`;

const RadioBox = styled.div<{ isMobile: boolean }>`
    .MuiFormGroup-root {
        flex-direction: row;
    }
    ${({ isMobile }) =>
        isMobile &&
        `
        margin-bottom: 30px;
        margin-top: 20px;
    `}
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 180px;
    width: 100%;
`;

const defaultCheckboxValue: statusTypes[] = [
    'Submitted',
    'Delivered',
    'Opened',
    'Clicked',
    'Unsubscribed',
    'Bounced',
    'Complaints',
    'Suppressed',
];

class SummaryGraphContent extends Component<Props, SummaryGraphContentState> {
    public state = {
        selectedChannel: null,
        selectedCampaign: null,
        dateFilterValue: 30,
        from: null,
        to: null,
        statuses: defaultCheckboxValue,
        currentCheckedStatuses: [],
        chartType: 'quantity' as chartTypes,
        searchChannel: '',
    };

    public componentDidMount(): void {
        const { LoadSummaryStart, LoadChannelsStart, account } = this.props;
        const query = parseUrlParams(location.search);
        const params: LoadSummaryStartParams = {
            from: this.state.from || dayjs().subtract(30, 'day').startOf('day').utc().format(),
            to: this.state.to || dayjs().endOf('day').utc().format(),
        };

        let selectedOption: number | string = 30;
        let queryStatuses: statusTypes[] = null;

        // if (isEmpty(query)) {
        //     return;
        // }

        if (query.statuses) {
            queryStatuses = flatten([query.statuses]) as statusTypes[];
        }

        if (query?.from && query?.to) {
            const from = dayjs.utc(query?.from as string);
            const to = dayjs.utc(query?.to as string);
            let dateDiff = to.diff(from, 'day');
            if (dateDiff > 28) {
                dateDiff = to.diff(from, 'month');
            }
            const dateOptions: Record<string, any>[] = this.getDateDropdownOptions();

            selectedOption = dateOptions?.find((el) => +el.value === +dateDiff)?.value || 'calendar';

            params.from = from.toJSON();
            params.to = setAtEndOfDay(to.toJSON());
        }

        if (query?.channel || query?.campaign) {
            params.channelName = (query?.channel || query?.campaign) as string;
        }

        this.setState(
            {
                from: params.from,
                to: params.to,
                statuses: queryStatuses || defaultCheckboxValue,
                currentCheckedStatuses: queryStatuses?.length === defaultCheckboxValue.length ? [] : queryStatuses,
                selectedChannel: params.channelName,
                dateFilterValue: selectedOption,
            },
            () => {
                if (account?.GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ViewChannels)) {
                    LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset: 0, skipSagaURL: true, from: this.state.from, to: this.state.to });
                }
                LoadSummaryStart(params);
                this.loadCampaigns();
            }
        );
    }

    private loadCampaigns(): void {
        const {
            account: { GeneralInfo },
            GetCampaigns,
        } = this.props;
        const { from, to } = this.state;
        if (GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ViewCampaigns)) {
            return GetCampaigns({ limit: 0, offset: 0, from, to });
        }
        return null;
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private handleFiltersCheckboxClick(event: SyntheticEvent<HTMLInputElement>, statuses): void {
        const { chartType } = this.state;
        const newStatuses = !statuses.length
            ? defaultCheckboxValue.filter((el) => (chartType === 'quality' ? el !== 'Submitted' : true))
            : statuses;

        this.addSearchQuery({ statuses: newStatuses });

        this.setState({
            statuses: newStatuses,
            currentCheckedStatuses: statuses,
        });
    }

    private renderRadioPanel = (): JSX.Element => {
        const { t } = this.props;
        return (
            <Radio
                name="radioQuantity"
                defaultValue="quantity"
                radios={[
                    {
                        label: t('byquantity'),
                        value: 'quantity',
                    },
                    {
                        label: t('byquality'),
                        value: 'quality',
                    },
                ]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { statuses } = this.state;
                    let newStatuses;
                    let currentCheckedStatuses;

                    if (e.currentTarget.value === 'quantity') {
                        newStatuses = clone(statuses);
                        if (!statuses.includes('Submitted')) {
                            newStatuses.unshift('Submitted');
                        }
                        currentCheckedStatuses = newStatuses.length === defaultCheckboxValue.length ? [] : newStatuses;
                    } else {
                        newStatuses = statuses.filter((el) => el !== 'Submitted');
                        currentCheckedStatuses = newStatuses.length === defaultCheckboxValue.length - 1 ? [] : newStatuses;
                    }

                    this.setState({
                        chartType: e.currentTarget.value as any,
                        statuses: newStatuses,
                        currentCheckedStatuses,
                    });
                }}
            />
        );
    };

    private formatDate = (date): string => {
        return convertToLocalTime(date, 'L');
    };

    private prepareChartsData = (): Record<string, any>[] => {
        const dailyData = this.props.statistics?.summary?.DailyLogStatusSummary;
        const { chartType } = this.state;
        const suffix = chartType === 'quantity' ? '' : 'Percentage';

        if (!dailyData) {
            return [];
        }

        const filteredData = dailyData.filter((el) => !!el);

        return filteredData?.map((el) => ({
            Date: this.formatDate(el.Date),
            Submitted: chartType === 'quantity' ? el.Email : 100,
            Bounced: el[`Bounced${suffix}`],
            Clicked: el[`Clicked${suffix}`],
            Complaints: el[`Complaint${suffix}`],
            Delivered: el[`Delivered${suffix}`],
            Opened: el[`Opened${suffix}`],
            Unsubscribed: el[`Unsubscribed${suffix}`],
            Suppressed: el[`Suppressed${suffix}`],
        }));
    };

    private isAnyGraphData = (data): number => {
        let dataValues = 0;
        const { chartType } = this.state;

        forEach(data, (el) => {
            for (const property in el) {
                if ((property !== 'Date' && chartType === 'quantity') || (chartType === 'quality' && property !== 'Submitted')) {
                    dataValues += el[property];
                }
            }
        });
        return dataValues;
    };

    private preparePercentageLegendData = (): { [key: string]: number | string } => {
        const logData = this.props.statistics?.summary?.LogStatusSummary;

        return {
            Submitted: logData?.EmailTotal === 0 ? `0%` : `100%`,
            Bounced: `${logData?.FailedPercentage}%`,
            Clicked: `${logData?.ClickedPercentage}%`,
            Complaints: `${logData?.FailedAbusePercentage}%`,
            Delivered: `${logData?.DeliveredPercentage}%`,
            Opened: `${logData?.OpenedPercentage}%`,
            Unsubscribed: `${logData?.UnsubscribedPercentage}%`,
            Suppressed: `${logData?.SuppressedPercentage}%`,
        };
    };

    private onLegendItemClick = (statuses): void => {
        const newStatuses = this.state.chartType === 'quality' ? statuses.filter((el) => el !== 'Submitted') : statuses;
        const currentCheckedStatuses =
            newStatuses.length === (this.state.chartType === 'quality' ? defaultCheckboxValue.length - 1 : defaultCheckboxValue.length)
                ? []
                : newStatuses;

        this.addSearchQuery({ statuses: newStatuses });

        this.setState({ statuses: newStatuses, currentCheckedStatuses });
    };

    private renderChart = (): JSX.Element => {
        const { t } = this.props;
        const { summaryLoading, summary } = this.props.statistics;
        const { statuses } = this.state;

        if (summaryLoading && isEmpty(summary)) {
            return (
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            );
        }

        const { browser } = this.props;
        const isMobile = browser.lessThan.hd;
        const chartData = this.prepareChartsData();
        const percentageLegendData = this.preparePercentageLegendData();
        const hasAnyGraphData = this.isAnyGraphData(chartData);

        const chartLineConfig = [
            { color: palette.blue[3], dataKey: 'Submitted' },
            { color: palette.indigo[6], dataKey: 'Delivered' },
            { color: palette.teal[6], dataKey: 'Opened' },
            { color: palette.teal[4], dataKey: 'Clicked' },
            { color: palette.purple[6], dataKey: 'Unsubscribed' },
            { color: palette.red[4], dataKey: 'Bounced' },
            { color: palette.gray[6], dataKey: 'Complaints' },
            { color: palette.gray[4], dataKey: 'Suppressed' },
        ];
        const needExpandedYAxis = chartData?.some((singleDay) => singleDay.Submitted / 1000 > 1);
        const mobileHeight = !hasAnyGraphData ? 400 : 900;
        return (
            <LineChart
                activeLinesFromLegend={statuses}
                lineConfig={chartLineConfig}
                data={chartData}
                isEmpty={!hasAnyGraphData}
                emptyDescription={t('emptystate')}
                percentageData={percentageLegendData}
                showPercentageValues={this.state.chartType === 'quality'}
                height={isMobile ? mobileHeight : 490}
                xAxisGroupName="Date"
                yAllowDecimals={false}
                showLegend
                isMobile={isMobile}
                padding={{ left: 3, right: 5 }}
                fullWidth
                onLegendsItemClick={this.onLegendItemClick.bind(this)}
                YAxisProps={{
                    width: needExpandedYAxis ? 75 : 45,
                }}
            />
        );
    };

    private getDateDropdownOptions = () => {
        const { t } = this.props;

        const calendar = [
            {
                label: t('specificdaterange'),
                value: 'calendar',
                borderBottom: true,
            },
        ];

        const dates = [
            {
                label: t('last7days'),
                value: '7',
            },
            {
                label: t('last14days'),
                value: '14',
            },
            {
                label: t('last30days'),
                value: '30',
            },
            {
                label: t('last3months'),
                value: '90',
            },
            {
                label: t('last6months'),
                value: '180',
            },
            {
                label: t('lastyear'),
                value: '365',
            },
        ];
        return this.isMobile() ? [...dates?.map((el) => ({ ...el, value: `${el.value}` })), ...calendar] : [...calendar, ...dates];
    };

    private renderDateRange = (): JSX.Element => {
        const { LoadSummaryStart } = this.props;
        const dateRangeData = this.getDateDropdownOptions();
        const query = parse(location.search);
        const dateFilterValue = !query.from ? DateDropdownOptions.Last30days : getDateDropdownValue(false, DateDropdownOptions.Last30days);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        return (
            <DateDropdown
                onItemClick={(e, obj): void => {
                    const dateValue = obj.value as string;
                    const to = dayjs.utc().hour(0).minute(0);
                    let from = null;

                    if (dateValue !== 'calendar') {
                        switch (dateValue) {
                            case '90':
                                from = dayjs().subtract(90, 'day').add(1, 'day').startOf('day').utc();
                                break;
                            case '180':
                                from = dayjs().subtract(180, 'day').add(1, 'day').startOf('day').utc();
                                break;
                            case '365':
                                from = dayjs().subtract(1, 'year').add(1, 'day').startOf('day').utc();
                                break;
                            default:
                                from = dayjs().subtract(parseInt(dateValue), 'd').add(1, 'day').startOf('day').utc();
                        }

                        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
                        this.setState({ from: from.format(), to: to.format(), dateFilterValue: dateValue }, (): void => {
                            this.state.selectedChannel
                                ? LoadSummaryStart({
                                      from: from.format(),
                                      to: to.format(),
                                      channelName: this.state.selectedChannel,
                                  })
                                : LoadSummaryStart({ from: from.format(), to: to.format() });
                            this.loadCampaigns();
                            this.fetchNextChannels(0);
                        });
                    }
                }}
                type="input"
                maxHeight={500}
                //minWidth={150}
                value={`${dateFilterValue}`}
                returnRawData
                buttonVariant="tertiary"
                options={dateRangeData}
                calendarOnChange={(fromTime: Date, toTime: Date): void => {
                    const from = dayjs(fromTime).startOf('day');
                    const to = dayjs(toTime).utc();

                    this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
                    this.setState({ from: from.toJSON(), to: to.toJSON() }, (): void => {
                        this.state.selectedChannel
                            ? LoadSummaryStart({
                                  from: from.format(LONG_DATE_FORMAT),
                                  to: to.format(LONG_DATE_FORMAT),
                                  channelName: this.state.selectedChannel,
                              })
                            : LoadSummaryStart({ from: from.toJSON(), to: to.toJSON() });
                        this.loadCampaigns();
                    });
                }}
                placement={BOTTOM_END}
                startDate={startDate}
                endDate={endDate}
            />
        );
    };

    private getFilterOptions = (): SimplePopoverPlainOption[] => {
        const { t } = this.props;
        return [
            {
                label: t('submitted'),
                value: 'Submitted',
            },
            {
                label: t('delivered'),
                value: 'Delivered',
            },
            {
                label: t('opened'),
                value: 'Opened',
            },
            {
                label: t('clicked'),
                value: 'Clicked',
            },
            {
                label: t('unsubscribed'),
                value: 'Unsubscribed',
            },
            {
                label: t('bounced'),
                value: 'Bounced',
            },
            {
                label: t('complaints'),
                value: 'Complaints',
            },
            {
                label: t('suppressed'),
                value: 'Suppressed',
            },
        ];
    };

    private renderFilterSelect = (): JSX.Element => {
        const { t } = this.props;
        const { chartType, currentCheckedStatuses } = this.state;
        const filters = this.getFilterOptions();

        if (chartType === 'quality') {
            filters.shift();
        }

        return (
            <SimplePopover
                type="noborder"
                listItemWidth="150px"
                options={filters}
                enableCheckbox={true}
                checkboxValue={currentCheckedStatuses}
                onCheckboxClick={this.handleFiltersCheckboxClick.bind(this)}
                placement={BOTTOM_END}
                defaultPlaceholder={t('filters')}
            />
        );
    };
    private isMobile(): boolean {
        const { browser } = this.props;
        return browser?.lessThan?.hd;
    }

    private prepareChannelsList = (): Record<string, string | boolean>[] => {
        const { t } = this.props;
        const { channels, loading } = this.props.channels;
        const { searchChannel } = this.state;
        if (isEmpty(channels) && !searchChannel && !loading) {
            return [{ value: 'all', label: 'All', borderBottom: true, key: 'all' }];
        }

        const channelsList = map(channels, (channel) => ({
            value: channel.Name,
            label: channel.Name,
            key: channel.Name,
            borderBottom: false,
        }));

        const search: any = [{ value: 'search', label: 'search' }];
        if (isEmpty(channelsList) || (loading && searchChannel && isEmpty(channelsList))) {
            return [
                ...search,
                ...(!loading
                    ? [
                          {
                              label: (
                                  <Typography variant="body1" bodyVariant="disabled">
                                      {t('noresults')}
                                  </Typography>
                              ),
                              disabled: true,
                              value: 0,
                          },
                      ]
                    : []),
            ];
        }

        if (this.isMobile()) {
            return channelsList;
        }

        const allChannels = !searchChannel ? [{ value: 'all', label: 'All', borderBottom: true, key: 'all' }] : [];

        return [...search, ...allChannels, ...channelsList];
    };

    private canSeeCampaigns(): boolean {
        const {
            GeneralInfo: { AccountAccessLevel },
        } = this.props?.account;
        return [AccessLevel.ViewCampaigns]?.every((access) => AccountAccessLevel?.includes(access));
    }

    private prepareCampaignList = (): Record<string, string | boolean>[] => {
        const { campaignsList } = this.props?.campaigns;

        if (isEmpty(campaignsList) || !this.canSeeCampaigns()) {
            return [];
        }

        const list = map(campaignsList, (campaign) => ({
            value: campaign.Name,
            label: campaign.Name,
            key: campaign.Name,
            borderBottom: false,
        }));

        if (this.isMobile()) {
            return list;
        }

        return [{ value: 'all', label: 'All', borderBottom: true, key: 'all' }, ...list];
    };

    private loadSummary(isAll: boolean): void {
        const { LoadSummaryStart } = this.props;

        if (isAll) {
            LoadSummaryStart({
                from: this.state.from,
                to: this.state.to,
            });

            return;
        }

        LoadSummaryStart({
            from: this.state.from,
            to: this.state.to,
            channelName: this.state.selectedChannel || this.state.selectedCampaign,
        });
    }

    private fetchNextChannels(customOffset?: number) {
        const { channels, hasMore } = this.props?.channels || {};
        const { searchChannel: search, from, to } = this.state;
        const offset = customOffset ?? channels?.length;
        !offset && this.props.ClearChannels();
        (hasMore || !offset) && this.props.LoadChannelsStart({ limit: INITIAL_PER_PAGE, offset, search, skipSagaURL: true, from, to });
    }

    private renderChannelsSelect = (): JSX.Element => {
        const { t, channels } = this.props;
        const channelsToShow = this.prepareChannelsList();

        return (
            <div style={{ display: 'inline-block', margin: '0 8px' }}>
                <SimplePopover
                    type="noborder"
                    maxHeight={400}
                    listItemWidth="400px"
                    options={channelsToShow as any}
                    onItemClick={(ev, channel): void => {
                        const isAll = channel.value === 'all';
                        const selectedChannel = isAll ? null : channel.value;
                        this.addSearchQuery({ channel: selectedChannel, campaign: null });

                        this.setState({ selectedChannel, selectedCampaign: null }, (): void => {
                            this.loadSummary(isAll);
                        });
                    }}
                    infiniteScrollProps={{
                        next: this.fetchNextChannels.bind(this),
                        hasMore: channels?.hasMore ?? true,
                        dataLength: channelsToShow?.length || INITIAL_PER_PAGE,
                    }}
                    onOptionSearch={(ev) => {
                        const target = ev?.target?.value || '';
                        debounce(() => {
                            this.setState({ searchChannel: target }, () => {
                                this.fetchNextChannels(0);
                            });
                        });
                    }}
                    rowsPerChunk={10}
                    cachedListSize={10}
                    placement={BOTTOM_END}
                    defaultPlaceholder={t('channels')}
                    listItemMaxLines={3}
                    keepSearchPhrase
                    isLoadingOptions={channels?.loading}
                />
            </div>
        );
    };

    private renderCampaignsSelect = (): JSX.Element => {
        const { t } = this.props;
        const campaigns = this.prepareCampaignList();
        if (!this.canSeeCampaigns() || isEmpty(campaigns)) {
            return null;
        }
        return (
            <div style={{ display: 'inline-block', margin: '0 8px' }}>
                <SimplePopover
                    type="noborder"
                    maxHeight={400}
                    listItemWidth="400px"
                    options={campaigns as any}
                    onItemClick={(ev, campaign): void => {
                        const isAll = campaign.value === 'all';
                        const selectedCampaign = isAll ? null : (campaign.value as string);
                        this.addSearchQuery({ campaign: selectedCampaign, channel: null });

                        this.setState({ selectedChannel: null, selectedCampaign }, (): void => {
                            this.loadSummary(isAll);
                        });
                    }}
                    placement={BOTTOM_END}
                    defaultPlaceholder={t('campaigns')}
                    listItemMaxLines={3}
                />
            </div>
        );
    };

    private renderMobileGraphHeader = (): JSX.Element => {
        const { t, channels } = this.props;
        const { currentCheckedStatuses, selectedCampaign, selectedChannel } = this.state;
        const channelList = this.prepareChannelsList();
        const campaignList = this.prepareCampaignList();
        const dateRanges = this.getDateDropdownOptions();
        const availableStatuses = this.getFilterOptions();
        const defaultDateValue = getDateDropdownValue(false, DateDropdownOptions.Last30days);
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        const combinedChannelsOptions = [...channelList, ...campaignList]?.sort();
        const channelFilterText = this.canSeeCampaigns() ? t('channels_campaigns') : t('channels');

        return (
            <GraphHeader>
                <Typography variant="h2" style={{ marginRight: 8 }}>
                    {truncate(this.state.selectedChannel, {
                        length: 52,
                    }) || t('allsent')}
                </Typography>
                {channels?.totalCount ? (
                    <FiltersPopover
                        onApply={(obj, from, to): void => {
                            this.mobileFiltersApply(obj, from, to);
                        }}
                        tabs={[
                            {
                                label: t('daterange'),
                                defaultValue: defaultDateValue.toString(),
                                options: dateRanges,
                                type: 'date',
                                startDate,
                                endDate,
                            },
                            {
                                defaultValue: selectedChannel || selectedCampaign || 'all',
                                label: channelFilterText,
                                options: combinedChannelsOptions as any,
                                type: 'radio',
                            },
                            {
                                defaultCheckboxValue: currentCheckedStatuses,
                                label: t('filters'),
                                options: availableStatuses as any,
                                type: 'checkbox',
                            },
                        ]}
                        title={t('filters')}
                        translations={{
                            apply: t('apply'),
                            close: t('close'),
                            to: t('to'),
                        }}
                    >
                        <FilterButton startIcon={<FilterIcon color={palette.gray[6]} />} />
                    </FiltersPopover>
                ) : null}
            </GraphHeader>
        );
    };

    private getFromValue(dateValue): Dayjs {
        switch (dateValue) {
            case '90':
                return dayjs().subtract(3, 'month').utc().hour(0).minute(0);
            case '180':
                return dayjs().subtract(6, 'month').utc().hour(0).minute(0);
            case '365':
                return dayjs().subtract(1, 'year').utc().hour(0).minute(0);
            default:
                return dayjs().subtract(dateValue, 'd').utc().hour(0).minute(0);
        }
    }

    private isSelectedStatusesEmpty(statuses: string[]): boolean {
        return (statuses?.length === 1 && !statuses?.[0]) || isEmpty(statuses);
    }

    private mobileFiltersApply = (filtersObj, newFrom, newTo): void => {
        const { LoadSummaryStart } = this.props;
        const dateValue = filtersObj[0];
        const newChannelName = filtersObj[1];
        const selectedStatuses = filtersObj[2];
        const areNewStatusesEmpty = this.isSelectedStatusesEmpty(selectedStatuses);
        const newStatuses = areNewStatusesEmpty ? defaultCheckboxValue : filtersObj[2];

        //check dateRange tab
        let to = dayjs.utc();
        let from = this.getFromValue(dateValue);

        if (dateValue === 'calendar') {
            from = newFrom ? dayjs(newFrom).startOf('day').add(1, 'day').utc() : dayjs().startOf('day').utc();
            to = newTo ? dayjs(newTo).add(1, 'day').hour(0).minute(0).second(0).utc() : dayjs.utc();
        }

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });

        this.setState({ from: from.toJSON(), to: to.toJSON(), dateFilterValue: dateValue }, (): void => {
            this.state.selectedChannel
                ? LoadSummaryStart({ from: from.toJSON(), to: to.toJSON(), channelName: this.state.selectedChannel })
                : LoadSummaryStart({ from: from.toJSON(), to: to.toJSON() });
        });

        //check channels tab
        if (!(this.state.selectedChannel === null && newChannelName === 'all') && this.state.selectedChannel !== newChannelName) {
            const isAll = newChannelName === 'all';
            const selectedChannel = isAll ? null : newChannelName;

            this.addSearchQuery({ channel: selectedChannel, campaign: null });
            this.setState({ selectedChannel, selectedCampaign: null }, (): void => {
                this.loadSummary(isAll);
            });
        }

        //check filters tab
        if (!isEqual(sortBy(this.state.statuses), sortBy(newStatuses))) {
            this.addSearchQuery({ statuses: newStatuses });
            this.setState({
                statuses: newStatuses,
                currentCheckedStatuses: selectedStatuses,
            });
        }
    };

    public render(): JSX.Element {
        const { browser, t, channels } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <GraphContent>
                {isMobile ? (
                    this.renderMobileGraphHeader()
                ) : (
                    <GraphHeader>
                        <Typography variant="h2" className="channelName">
                            {this.state.selectedChannel || this.state.selectedCampaign || t('allsent')}
                        </Typography>
                        {channels?.totalCount ? (
                            <GraphFilters>
                                {this.renderDateRange()}
                                {this.renderChannelsSelect()}
                                {this.renderCampaignsSelect()}
                                {this.renderFilterSelect()}
                            </GraphFilters>
                        ) : null}
                    </GraphHeader>
                )}
                {channels?.totalCount ? <RadioBox isMobile={isMobile}>{this.renderRadioPanel()}</RadioBox> : null}
                <div style={{ position: 'relative' }}>{this.renderChart()}</div>
            </GraphContent>
        );
    }
}

export default compose(withTranslation(), connect<StatisticsSummaryInterface, DispatchProps>(mapState, dispatchProps))(SummaryGraphContent);
